import { useState, useEffect, type CSSProperties } from "react";
import { useAuth } from "../context/AuthProvider";
import {
  adminListPremiumFrames,
  adminCreatePremiumFrame,
  adminUpdatePremiumFrame,
  adminDeletePremiumFrame,
  adminListPremiumBadges,
  adminCreatePremiumBadge,
  adminUpdatePremiumBadge,
  adminDeletePremiumBadge,
  adminListPremiumBackgrounds,
  adminCreatePremiumBackground,
  adminUpdatePremiumBackground,
  adminDeletePremiumBackground,
} from "../lib/api";

// ============================================================================
// Type Definitions
// ============================================================================

type Tab = "frames" | "badges" | "backgrounds";
type Mode = "create" | "edit";

interface PremiumItem {
  _id: string;
  name: string;
  description: string;
  imageUrl: string;
  price: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface FormData {
  name: string;
  description: string;
  imageUrl: string;
  price: string;
  isActive: boolean;
}

const emptyFormData: FormData = {
  name: "",
  description: "",
  imageUrl: "",
  price: "0",
  isActive: true,
};

// ============================================================================
// Main Component
// ============================================================================

export default function AdminPremium() {
  const { user } = useAuth();

  // Tab State
  const [activeTab, setActiveTab] = useState<Tab>("frames");

  // Data State
  const [frames, setFrames] = useState<PremiumItem[]>([]);
  const [badges, setBadges] = useState<PremiumItem[]>([]);
  const [backgrounds, setBackgrounds] = useState<PremiumItem[]>([]);

  // UI State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form State
  const [mode, setMode] = useState<Mode>("create");
  const [selectedItem, setSelectedItem] = useState<PremiumItem | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyFormData);

  const canUse = !!user?.isAdmin;

  useEffect(() => {
    if (canUse) {
      loadAllData();
    }
  }, [canUse]);

  // Load all premium items
  async function loadAllData() {
    setLoading(true);
    setError(null);
    try {
      const [framesRes, badgesRes, backgroundsRes] = await Promise.all([
        adminListPremiumFrames(),
        adminListPremiumBadges(),
        adminListPremiumBackgrounds(),
      ]);

      setFrames(framesRes.frames || []);
      setBadges(badgesRes.badges || []);
      setBackgrounds(backgroundsRes.backgrounds || []);
    } catch (e: any) {
      console.error("Load premium data error:", e);
      setError(e.message || "Veriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  }

  // Get current items based on active tab
  function getCurrentItems(): PremiumItem[] {
    if (activeTab === "frames") return frames;
    if (activeTab === "badges") return badges;
    return backgrounds;
  }

  // Handle edit button click
  function handleEdit(item: PremiumItem) {
    setMode("edit");
    setSelectedItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      imageUrl: item.imageUrl,
      price: String(item.price),
      isActive: item.isActive,
    });
  }

  // Handle cancel edit
  function handleCancelEdit() {
    setMode("create");
    setSelectedItem(null);
    setFormData(emptyFormData);
  }

  // Handle form submit
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      alert("İsim gerekli!");
      return;
    }
    if (!formData.description.trim()) {
      alert("Açıklama gerekli!");
      return;
    }
    if (!formData.imageUrl.trim()) {
      alert("Görsel URL'i gerekli!");
      return;
    }

    const price = Number(formData.price);
    if (isNaN(price) || price < 0) {
      alert("Geçerli bir fiyat girin!");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        imageUrl: formData.imageUrl.trim(),
        price,
        isActive: formData.isActive,
      };

      if (mode === "create") {
        // Create new item
        if (activeTab === "frames") {
          await adminCreatePremiumFrame(payload);
        } else if (activeTab === "badges") {
          await adminCreatePremiumBadge(payload);
        } else {
          await adminCreatePremiumBackground(payload);
        }
        alert("✅ Başarıyla oluşturuldu!");
      } else {
        // Update existing item
        if (!selectedItem) return;
        if (activeTab === "frames") {
          await adminUpdatePremiumFrame(selectedItem._id, payload);
        } else if (activeTab === "badges") {
          await adminUpdatePremiumBadge(selectedItem._id, payload);
        } else {
          await adminUpdatePremiumBackground(selectedItem._id, payload);
        }
        alert("✅ Başarıyla güncellendi!");
      }

      // Reload data and reset form
      await loadAllData();
      handleCancelEdit();
    } catch (e: any) {
      console.error("Save error:", e);
      setError(e.message || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  }

  // Handle delete
  async function handleDelete(item: PremiumItem) {
    const itemType = activeTab === "frames" ? "Frame" : activeTab === "badges" ? "Badge" : "Background";
    
    if (!confirm(`"${item.name}" ${itemType}'ini silmek istediğinizden emin misiniz?`)) {
      return;
    }

    setError(null);

    try {
      if (activeTab === "frames") {
        await adminDeletePremiumFrame(item._id);
      } else if (activeTab === "badges") {
        await adminDeletePremiumBadge(item._id);
      } else {
        await adminDeletePremiumBackground(item._id);
      }

      alert("✅ Başarıyla silindi!");
      await loadAllData();

      // If deleted item was being edited, reset form
      if (selectedItem?._id === item._id) {
        handleCancelEdit();
      }
    } catch (e: any) {
      console.error("Delete error:", e);
      alert("❌ Silme başarısız: " + e.message);
    }
  }

  // Admin access check
  if (!canUse) {
    return (
      <div style={containerStyle}>
        <h2>Admin · Premium Items</h2>
        <p>Bu sayfa için admin yetkisi gerekli.</p>
      </div>
    );
  }

  const currentItems = getCurrentItems();

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h1 style={titleStyle}>💎 Admin · Premium Items</h1>
          <p style={subtitleStyle}>
            Buradan Premium Frames, Badges ve Backgrounds içeriklerini yönetebilirsin.
          </p>
        </div>
        <button onClick={loadAllData} style={refreshButtonStyle} disabled={loading}>
          🔄 Yenile
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div style={errorBannerStyle}>
          <span>⚠️ {error}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => loadAllData()} style={retryButtonStyle}>
              🔄 Yeniden Dene
            </button>
            <button onClick={() => setError(null)} style={closeButtonStyle}>
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Tab Switcher */}
      <div style={tabContainerStyle}>
        <button
          onClick={() => setActiveTab("frames")}
          style={activeTab === "frames" ? activeTabStyle : inactiveTabStyle}
        >
          🖼️ Frames
        </button>
        <button
          onClick={() => setActiveTab("badges")}
          style={activeTab === "badges" ? activeTabStyle : inactiveTabStyle}
        >
          🏅 Badges
        </button>
        <button
          onClick={() => setActiveTab("backgrounds")}
          style={activeTab === "backgrounds" ? activeTabStyle : inactiveTabStyle}
        >
          🎨 Backgrounds
        </button>
      </div>

      {/* Content */}
      {loading && currentItems.length === 0 ? (
        <div style={loadingContainerStyle}>
          <div className="spinner" style={{ marginBottom: 16 }} />
          <p style={{ color: "var(--muted)" }}>Yükleniyor...</p>
        </div>
      ) : (
        <div style={contentContainerStyle}>
          {/* List Section */}
          <div style={listSectionStyle}>
            <h3 style={sectionTitleStyle}>
              Mevcut {activeTab === "frames" ? "Frames" : activeTab === "badges" ? "Badges" : "Backgrounds"} ({currentItems.length})
            </h3>

            {currentItems.length === 0 ? (
              <div style={emptyStateStyle}>
                <span style={{ fontSize: 48, marginBottom: 16 }}>📦</span>
                <p>Henüz kayıt yok</p>
              </div>
            ) : (
              <div style={itemsGridStyle}>
                {currentItems.map((item) => (
                  <div key={item._id} style={itemCardStyle}>
                    {/* Image Preview */}
                    <div style={imagePreviewStyle}>
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: 32 }}>🖼️</span>
                      )}
                    </div>

                    {/* Item Info */}
                    <div style={{ padding: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <h4 style={{ fontSize: 16, fontWeight: "bold", margin: 0, flex: 1 }}>
                          {item.name}
                        </h4>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: "bold",
                            padding: "4px 8px",
                            borderRadius: 4,
                            background: item.isActive ? "#10b981" : "#6b7280",
                            color: "#fff",
                          }}
                        >
                          {item.isActive ? "Aktif" : "Pasif"}
                        </span>
                      </div>

                      <p
                        style={{
                          fontSize: 13,
                          color: "var(--muted)",
                          margin: "4px 0 8px",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.description}
                      </p>

                      <div style={{ fontSize: 14, fontWeight: "bold", color: "var(--primary)", marginBottom: 12 }}>
                        💰 {item.price} TL
                      </div>

                      {/* Actions */}
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() => handleEdit(item)}
                          style={{
                            ...actionButtonStyle,
                            background: "#3b82f6",
                            color: "#fff",
                            flex: 1,
                          }}
                        >
                          ✏️ Düzenle
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          style={{
                            ...actionButtonStyle,
                            background: "#ef4444",
                            color: "#fff",
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form Section */}
          <div style={formSectionStyle}>
            <div style={formHeaderStyle}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: "bold" }}>
                {mode === "create" ? (
                  `Yeni ${activeTab === "frames" ? "Frame" : activeTab === "badges" ? "Badge" : "Background"} Oluştur`
                ) : (
                  <>
                    <span style={{ fontSize: 14, color: "var(--muted)" }}>Düzenleme Modu</span>
                    <br />
                    {selectedItem?.name}
                  </>
                )}
              </h3>
              {mode === "edit" && (
                <button onClick={handleCancelEdit} style={cancelEditButtonStyle}>
                  ✕ İptal
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} style={formStyle}>
              {/* Name */}
              <div style={fieldStyle}>
                <label style={labelStyle}>İsim *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={inputStyle}
                  placeholder="Örn: Golden Frame"
                  required
                />
              </div>

              {/* Description */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Açıklama *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
                  placeholder="Detaylı açıklama..."
                  required
                />
              </div>

              {/* Image URL */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Görsel URL *</label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  style={inputStyle}
                  placeholder="https://..."
                  required
                />
                {formData.imageUrl && (
                  <div style={{ marginTop: 8, padding: 8, background: "var(--bg)", borderRadius: 8 }}>
                    <img
                      src={formData.imageUrl}
                      alt="Preview"
                      style={{ width: "100%", maxHeight: 200, objectFit: "contain", borderRadius: 4 }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Price */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Fiyat (TL) *</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  style={inputStyle}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              {/* Is Active */}
              <div style={{ ...fieldStyle, flexDirection: "row", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  style={{ width: 20, height: 20, cursor: "pointer" }}
                />
                <label style={{ ...labelStyle, marginBottom: 0 }}>Aktif</label>
              </div>

              {/* Submit Button */}
              <button type="submit" disabled={saving} style={submitButtonStyle}>
                {saving ? "⏳ Kaydediliyor..." : mode === "create" ? "✅ Oluştur" : "✅ Güncelle"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const containerStyle: CSSProperties = {
  maxWidth: 1400,
  margin: "0 auto",
  padding: "16px 24px",
  minHeight: "100vh",
};

const headerStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: 24,
  flexWrap: "wrap",
  gap: 16,
};

const titleStyle: CSSProperties = {
  fontSize: 28,
  fontWeight: "bold",
  marginBottom: 8,
  color: "var(--text)",
  background: "linear-gradient(135deg, #a855f7 0%, #ec4899 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

const subtitleStyle: CSSProperties = {
  fontSize: 14,
  color: "var(--muted)",
  margin: 0,
};

const refreshButtonStyle: CSSProperties = {
  padding: "8px 16px",
  background: "var(--bg2)",
  border: "1px solid var(--muted)",
  borderRadius: 8,
  color: "var(--text)",
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 600,
};

const errorBannerStyle: CSSProperties = {
  background: "#dc2626",
  color: "#fff",
  padding: "12px 16px",
  borderRadius: 8,
  marginBottom: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
};

const retryButtonStyle: CSSProperties = {
  background: "rgba(255, 255, 255, 0.2)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  color: "#fff",
  fontSize: 13,
  fontWeight: "bold",
  padding: "6px 12px",
  borderRadius: 4,
  cursor: "pointer",
};

const closeButtonStyle: CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#fff",
  fontSize: 20,
  cursor: "pointer",
  padding: "0 8px",
};

const tabContainerStyle: CSSProperties = {
  display: "flex",
  gap: 8,
  marginBottom: 24,
  borderBottom: "2px solid var(--muted)",
  paddingBottom: 4,
};

const activeTabStyle: CSSProperties = {
  padding: "12px 24px",
  background: "linear-gradient(135deg, #a855f7 0%, #ec4899 100%)",
  border: "none",
  borderRadius: "8px 8px 0 0",
  color: "#fff",
  fontSize: 14,
  fontWeight: "bold",
  cursor: "pointer",
};

const inactiveTabStyle: CSSProperties = {
  padding: "12px 24px",
  background: "transparent",
  border: "none",
  borderRadius: "8px 8px 0 0",
  color: "var(--muted)",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const loadingContainerStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "50vh",
};

const contentContainerStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 400px",
  gap: 24,
};

const listSectionStyle: CSSProperties = {
  minHeight: "50vh",
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 18,
  fontWeight: "bold",
  marginBottom: 16,
  color: "var(--text)",
};

const emptyStateStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 300,
  background: "var(--bg2)",
  border: "1px solid var(--muted)",
  borderRadius: 12,
  color: "var(--muted)",
};

const itemsGridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
  gap: 16,
};

const itemCardStyle: CSSProperties = {
  background: "var(--bg2)",
  border: "1px solid var(--muted)",
  borderRadius: 12,
  overflow: "hidden",
  transition: "transform 0.2s, box-shadow 0.2s",
};

const imagePreviewStyle: CSSProperties = {
  width: "100%",
  height: 150,
  background: "var(--bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
};

const actionButtonStyle: CSSProperties = {
  padding: "8px 12px",
  border: "none",
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const formSectionStyle: CSSProperties = {
  position: "sticky",
  top: 16,
  height: "fit-content",
  background: "var(--bg2)",
  border: "1px solid var(--muted)",
  borderRadius: 12,
  padding: 20,
};

const formHeaderStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: 20,
  paddingBottom: 16,
  borderBottom: "1px solid var(--muted)",
};

const cancelEditButtonStyle: CSSProperties = {
  padding: "6px 12px",
  background: "#6b7280",
  border: "none",
  borderRadius: 6,
  color: "#fff",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const formStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const fieldStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
};

const labelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--text)",
  marginBottom: 6,
};

const inputStyle: CSSProperties = {
  padding: "10px 12px",
  background: "var(--bg)",
  border: "1px solid var(--muted)",
  borderRadius: 8,
  color: "var(--text)",
  fontSize: 14,
};

const submitButtonStyle: CSSProperties = {
  padding: "12px 16px",
  background: "linear-gradient(135deg, #a855f7 0%, #ec4899 100%)",
  border: "none",
  borderRadius: 8,
  color: "#fff",
  fontSize: 14,
  fontWeight: "bold",
  cursor: "pointer",
  marginTop: 8,
};
